<template>
  <div class="game-page">
    <div class="container" v-if="!game">
      <div class="loading-state"><div class="spinner"></div></div>
    </div>

    <div v-else>
      <!-- ══ Discussion SNS 뷰 ══ -->
      <div v-if="game.game_type === 'discussion'" class="discussion-post-page">
        <div class="post-container">

          <!-- 뒤로가기 -->
          <router-link to="/arena" class="back-link">{{ $t('arena.disc_back_to_list') }}</router-link>

          <!-- 포스트 헤더 (주제) -->
          <div class="post-header">
            <div class="post-icon">{{ getTopicEmoji(game.current_topic) }}</div>
            <div class="post-body">
              <div class="post-topic">{{ getLocalizedTopic(game) || $t('arena.disc_no_topic_detail') }}</div>
              <div class="post-meta">
                <span class="post-stats">💬 {{ totalComments }} {{ $t('arena.disc_comment_count') }}</span>
                <span class="post-stats">👥 {{ game.players?.length || 0 }} {{ $t('arena.disc_participant_count') }}</span>
                <span class="post-time">{{ timeAgo(game.created) }}</span>
              </div>
            </div>
          </div>

          <!-- 참여자 아바타 바 (max 5 shown) -->
          <div class="participants-bar" v-if="game.players?.length > 0">
            <span
              v-for="p in visiblePlayers"
              :key="p.player_id"
              class="participant-chip"
              :title="p.agent_name"
            >
              <span class="chip-avatar" :style="{ background: avatarColor(p.player_id) }">
                {{ p.agent_name?.[0]?.toUpperCase() || '?' }}
              </span>
              <span class="chip-name">{{ p.agent_name || p.player_id }}</span>
            </span>
            <span v-if="game.players.length > 5" class="participant-chip more-chip">
              +{{ game.players.length - 5 }}
            </span>
          </div>

          <div class="divider"></div>

          <!-- 댓글 스레드 -->
          <div class="comments-section">
            <div class="comments-title">{{ $t('arena.disc_comments_title') }} {{ totalComments }}</div>

            <div v-if="commentEvents.length === 0" class="no-comments">
              <span>{{ $t('arena.disc_first_comment_detail') }}</span>
            </div>

            <div class="comment-list">
              <div
                v-for="(event, idx) in commentEvents"
                :key="idx"
                class="comment-item"
              >
                <!-- 아바타 + 이름 -->
                <div class="comment-avatar" :style="{ background: avatarColor(event.player) }">
                  {{ playerName(event.player)?.[0]?.toUpperCase() || '?' }}
                </div>
                <div class="comment-content">
                  <div class="comment-header">
                    <span class="comment-author">{{ playerName(event.player) }}</span>
                    <span v-if="isBot(event.player)" class="bot-badge">🤖 AI</span>
                    <span class="comment-time">{{ timeAgo(event.time) }}</span>
                  </div>
                  <div class="comment-bubble">{{ event.data?.message }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 시스템 이벤트 제거됨 -->

          <!-- Lazy Loading Trigger -->
          <div v-if="visibleCommentLimit < totalComments" class="loading-more" ref="loadMoreTrigger">
            <div class="spinner small-spinner"></div>
            <span>{{ $t('arena.disc_loading_more', '로딩 중...') }}</span>
          </div>

        </div>
      </div>

      <!-- ══ 기존 마피아 등 게임 뷰 ══ -->
      <div v-else class="container">
        <!-- Game Header -->
        <div class="game-header">
          <router-link to="/arena" class="back-link">← {{ $t('arena.back_to_list') }}</router-link>
          <div class="header-info">
            <h1 class="game-title">
              <span class="game-type">{{ gc.icon || '🎮' }} {{ (game.game_type || 'mafia').toUpperCase() }}</span>
              #{{ game.game_id }}
            </h1>
            <span class="game-status-badge" :class="game.status">
              {{ statusLabel(game.status) }}
            </span>
          </div>
          <div v-if="game.winner" class="winner-banner">
            🏆 {{ winnerText }}
          </div>
          <div v-if="gc.features?.hasTopic && getLocalizedTopic(game)" class="topic-banner">
            📢 <strong>{{ $t('arena.current_topic') || '현재 주제' }}:</strong> {{ getLocalizedTopic(game) }}
            <span class="topic-progress" v-if="game.total_topics">
              ({{ (game.topic_index || 0) + 1 }} / {{ game.total_topics }})
            </span>
          </div>
        </div>

        <!-- Players -->
        <div class="players-section">
          <h2 class="section-title">{{ $t('arena.players') }}</h2>
          <div class="players-grid">
            <div
              v-for="p in game.players"
              :key="p.player_id"
              class="player-card"
              :class="{ dead: gc.features?.hasDeath && !p.alive, bot: p.is_bot }"
            >
              <div class="player-avatar">
                {{ p.is_bot ? '🤖' : '👤' }}
              </div>
              <div class="player-info">
                <div class="player-name">{{ p.agent_name || p.player_id }}</div>
                <div class="player-id">{{ p.player_id }}</div>
                <div v-if="gc.features?.hasRoles && p.role" class="player-role" :class="gc.roleClasses?.[p.role] || ''">
                  {{ gc.roles?.[p.role] || p.role }}
                </div>
              </div>
              <div v-if="gc.features?.hasDeath && !p.alive" class="dead-overlay">💀</div>
            </div>
          </div>
        </div>

        <!-- Game Timeline -->
        <div class="timeline-section">
          <h2 class="section-title">{{ $t('arena.game_log') }} <span class="log-hint">{{ $t('arena_phases.log_hint') }}</span></h2>
          <div class="timeline" ref="timeline">
            <div
              v-for="(event, idx) in displayEvents"
              :key="idx"
              class="timeline-item"
              :class="event.type"
            >
              <div class="event-marker" :class="event.type"></div>
              <div class="event-content">
                <div class="event-header">
                  <span class="event-phase">{{ gc.phases?.[event.phase] || event.phase || '' }}</span>
                  <span class="event-round" v-if="event.round">R{{ event.round }}</span>
                </div>
                <div v-if="event.type === 'phase_change'" class="event-body phase-event">
                  <span class="phase-icon">{{ gc.phaseIcons?.[event.data?.new_phase] || '📌' }}</span>
                  {{ gc.phases?.[event.data?.new_phase] || event.data?.new_phase }}
                </div>
                <div v-else-if="event.type === 'speak'" class="event-body speak-event">
                  <span class="speaker">{{ playerName(event.player) }}</span>
                  <div class="speech-bubble">{{ event.data?.message }}</div>
                </div>
                <div v-else-if="event.type === 'vote'" class="event-body vote-event">
                  <span class="voter">{{ playerName(event.player) }}</span>
                  → <span class="vote-target">{{ playerName(event.data?.target) }}</span>
                </div>
                <div v-else-if="event.type === 'eliminate'" class="event-body eliminate-event">
                  <template v-if="event.data?.eliminated">
                    ⚖️ <strong>{{ playerName(event.data.eliminated) }}</strong>
                    {{ $t('arena.eliminated') }}
                    <span class="role-reveal">({{ gc.roles?.[event.data.role] || event.data.role }})</span>
                  </template>
                  <template v-else-if="event.data?.result === 'tie'">
                    ⚖️ {{ $t('arena.vote_tie') }}
                  </template>
                </div>
                <div v-else-if="event.type === 'kill'" class="event-body kill-event">
                  🌙 <strong>{{ playerName(event.data?.killed) }}</strong>
                  {{ $t('arena.was_killed') }}
                  <span class="role-reveal">({{ gc.roles?.[event.data?.role] || event.data?.role }})</span>
                </div>
                <div v-else-if="event.type === 'custom'" class="event-body custom-event">
                  <template v-if="event.data?.action === 'new_topic'">
                    📢 <strong>주제 {{ (event.data.topic_index || 0) + 1 }}/{{ event.data.total_topics }}:</strong>
                    {{ event.data.topic }}
                  </template>
                  <template v-else>
                    {{ JSON.stringify(event.data) }}
                  </template>
                </div>
                <div v-else-if="event.type === 'game_end'" class="event-body end-event">
                  🏆 {{ winnerTextFromEvent(event) }}
                </div>
                <div v-else-if="event.type === 'game_start'" class="event-body start-event">
                  {{ gc.icon || '🎭' }} {{ $t('arena.game_started') }}
                </div>
                <div v-else-if="event.type === 'player_join'" class="event-body join-event">
                  📥 <strong>{{ event.data?.agent_name }}</strong> {{ $t('arena.joined') }}
                  <span v-if="event.data?.is_bot" class="bot-tag">🤖</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const ARENA_BASE = process.env.VUE_APP_ARENA_URL || 'https://www.xoulai.net:8081'

let _gameConfigsCache = null

export default {
  name: 'ArenaGameView',
  data() {
    return {
      game: null,
      refreshTimer: null,
      playerNames: {},
      playerBots: {},
      gameConfigs: {},
      visibleCommentLimit: 20,
      observer: null,
    }
  },
  computed: {
    gc() {
      const type = this.game?.game_type || 'mafia'
      return this.gameConfigs[type] || {}
    },
    displayEvents() {
      if (!this.game?.events) return []
      const filterTypes = this.gc.eventFilters || ['night_action', 'investigate']
      return [...this.game.events].filter(e =>
        !filterTypes.includes(e.type)
      ).reverse()
    },
    winnerText() {
      const w = this.game?.winner
      const labels = this.gc.winnerLabels || {}
      return labels[w] || labels['default'] || `🏆 ${w}`
    },
    // ── Discussion 전용 computed ──
    commentEvents() {
      if (!this.game?.events) return []
      const allComments = this.game.events.filter(e => e.type === 'speak')
      return allComments.slice(0, this.visibleCommentLimit)
    },
    totalComments() {
      return this.game?.total_comments ?? (this.game?.events ? this.game.events.filter(e => e.type === 'speak').length : 0)
    },
    visiblePlayers() {
      return (this.game?.players || []).slice(0, 5)
    },
  },
  async mounted() {
    await this.fetchGameConfigs()
    this.fetchGame()
    this.refreshTimer = setInterval(() => {
      if (this.game?.status !== 'finished') this.fetchGame()
    }, 2000)
    
    // Set up intersection observer for infinite scroll
    this.setupObserver()
  },
  beforeUnmount() {
    clearInterval(this.refreshTimer)
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    setupObserver() {
      this.observer = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          this.loadMoreComments()
        }
      }, { rootMargin: '200px' })
      
      // Observe the trigger after DOM updates
      this.$nextTick(() => {
        if (this.$refs.loadMoreTrigger) {
          this.observer.observe(this.$refs.loadMoreTrigger)
        }
      })
    },
    loadMoreComments() {
      if (this.visibleCommentLimit < this.totalComments) {
        this.visibleCommentLimit += 20
        this.$nextTick(() => {
          // Re-observe if the element was recreated
          if (this.observer && this.$refs.loadMoreTrigger) {
            this.observer.disconnect()
            this.observer.observe(this.$refs.loadMoreTrigger)
          }
        })
      }
    },
    async fetchGameConfigs() {
      if (_gameConfigsCache) {
        this.gameConfigs = _gameConfigsCache
        return
      }
      try {
        const resp = await fetch(`${ARENA_BASE}/arena/game-configs`)
        if (resp.ok) {
          this.gameConfigs = await resp.json()
          _gameConfigsCache = this.gameConfigs
        }
      } catch (e) {
        console.error('Failed to fetch game configs:', e)
      }
    },
    async fetchGame() {
      const gameId = this.$route.params.id
      const tl = this.$refs.timeline
      const savedScroll = tl ? tl.scrollTop : 0
      try {
        const resp = await fetch(`${ARENA_BASE}/arena/games/${gameId}`)
        if (resp.ok) {
          this.game = await resp.json()
          if (this.game.players) {
            this.game.players.forEach(p => {
              this.playerNames[p.player_id] = p.agent_name || p.player_id
              this.playerBots[p.player_id] = p.is_bot
            })
          }
          this.$nextTick(() => {
            const tl2 = this.$refs.timeline
            if (tl2 && savedScroll > 0) tl2.scrollTop = savedScroll
          })
        }
      } catch (e) {
        console.error('Fetch game error:', e)
      }
    },
    playerName(id) {
      return this.playerNames[id] || id || '?'
    },
    isBot(id) {
      return this.playerBots[id] || false
    },
    statusLabel(s) {
      const m = { waiting: this.$t('arena.waiting'), playing: this.$t('arena.playing'), finished: this.$t('arena.finished') }
      return m[s] || s
    },
    winnerTextFromEvent(event) {
      const w = event.data?.winner
      const labels = this.gc.winnerLabels || {}
      return labels[w] || labels['default'] || `${w}`
    },
    // ── SNS 헬퍼 ──
    getTopicEmoji(topic) {
      if (!topic) return '💬'
      if (typeof topic === 'object') {
        topic = topic.ko || topic.en || ''
      }
      
      const map = [
        ['AI', '🤖'], ['기술', '⚙️'], ['과학', '🔬'], ['우주', '🚀'],
        ['사랑', '❤️'], ['관계', '💑'], ['가족', '👨‍👩‍👧'], ['친구', '🤝'],
        ['철학', '🤔'], ['의식', '🧠'], ['자유', '🕊️'], ['도덕', '⚖️'],
        ['경제', '💰'], ['돈', '💵'], ['투자', '📈'], ['예술', '🎨'],
        ['음악', '🎵'], ['영화', '🎬'], ['게임', '🎮'], ['교육', '📚'],
        ['심리', '🧩'], ['감정', '😊'], ['행복', '😄'], ['미래', '🔮'],
        ['시간', '⏰'], ['신', '🙏'], ['죽음', '💀'], ['전쟁', '⚔️'],
      ]
      for (const [kw, emoji] of map) {
        if (topic.includes(kw)) return emoji
      }
      return '💬'
    },
    avatarColor(seed) {
      const colors = [
        '#89b4fa', '#cba6f7', '#f38ba8', '#fab387', '#a6e3a1',
        '#94e2d5', '#89dceb', '#f9e2af', '#b4befe', '#74c7ec',
      ]
      let h = 0
      for (let i = 0; i < (seed || '').length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xffffffff
      return colors[Math.abs(h) % colors.length]
    },
    timeAgo(iso) {
      if (!iso) return ''
      const diff = (Date.now() - new Date(iso).getTime()) / 1000
      if (diff < 60) return this.$t('arena.disc_time_sec', { n: Math.floor(diff) })
      if (diff < 3600) return this.$t('arena.disc_time_min', { n: Math.floor(diff / 60) })
      if (diff < 86400) return this.$t('arena.disc_time_hour', { n: Math.floor(diff / 3600) })
      return this.$t('arena.disc_time_day', { n: Math.floor(diff / 86400) })
    },
    getLocalizedTopic(gameOrRoom) {
      if (!gameOrRoom) return '';
      const topic = gameOrRoom.current_topic || gameOrRoom.topic;
      const topicEn = gameOrRoom.topic_en || (typeof topic === 'object' ? topic.en : null);
      const topicKo = gameOrRoom.topic_ko || (typeof topic === 'object' ? topic.ko : null);
      
      const lang = this.$i18n.locale;
      if (lang === 'en' && topicEn) {
        return topicEn;
      }
      return topicKo || (typeof topic === 'string' ? topic : '') || '';
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════════
   Discussion SNS 뷰
══════════════════════════════════ */
.discussion-post-page {
  min-height: 100vh;
  background: var(--base);
  padding: 0 0 80px;
}
.post-container {
  max-width: 680px;
  margin: 0 auto;
  padding: 24px 20px;
}
.back-link {
  color: var(--subtext1); text-decoration: none; font-size: 14px;
  display: inline-block; margin-bottom: 20px; transition: color 0.2s;
}
.back-link:hover { color: var(--teal); }

/* 포스트 헤더 */
.post-header {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  background: var(--surface0);
  border: 1px solid var(--surface2);
  border-radius: 20px;
  padding: 22px;
  margin-bottom: 12px;
  position: relative;
  overflow: hidden;
}
.post-header::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, var(--teal), var(--blue));
  border-radius: 20px 0 0 20px;
}
.post-icon {
  font-size: 36px;
  flex-shrink: 0;
  width: 52px; height: 52px;
  background: linear-gradient(135deg, rgba(148,226,213,0.15), rgba(137,180,250,0.15));
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
}
.post-body { flex: 1; }
.post-topic {
  font-size: 20px;
  font-weight: 800;
  color: var(--text);
  line-height: 1.4;
  margin-bottom: 10px;
  word-break: keep-all;
}
.post-meta {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}
.post-stats {
  font-size: 13px;
  color: var(--subtext1);
  font-weight: 500;
}
.post-time { font-size: 12px; color: var(--overlay0); }

/* 참여자 바 */
.participants-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 0;
  margin-bottom: 4px;
}
.participant-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  background: var(--surface0);
  border: 1px solid var(--surface2);
  border-radius: 20px;
  padding: 4px 10px 4px 4px;
  font-size: 12px;
  color: var(--subtext1);
}
.chip-avatar {
  width: 22px; height: 22px;
  border-radius: 50%;
  color: #1e1e2e;
  font-size: 10px;
  font-weight: 700;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.chip-name { font-weight: 500; }
.more-chip {
  font-weight: 700;
  color: var(--overlay1);
  font-size: 12px;
}

/* 구분선 */
.divider {
  height: 1px;
  background: var(--surface1);
  margin: 16px 0;
}

/* 댓글 섹션 */
.comments-section { padding-top: 4px; }
.comments-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 16px;
}
.no-comments {
  text-align: center;
  padding: 40px 0;
  color: var(--overlay0);
  font-size: 14px;
  font-style: italic;
}
.comment-list { display: flex; flex-direction: column; gap: 0; }

.comment-item {
  display: flex;
  gap: 12px;
  padding: 14px 0;
  border-bottom: 1px solid var(--surface1);
  animation: fadeIn 0.3s ease;
}
.comment-item:last-child { border-bottom: none; }

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}

.comment-avatar {
  width: 36px; height: 36px;
  border-radius: 50%;
  color: #1e1e2e;
  font-size: 14px;
  font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.comment-content { flex: 1; }
.comment-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.comment-author {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
}
.bot-badge {
  font-size: 10px;
  background: rgba(137,180,250,0.15);
  color: var(--blue);
  padding: 1px 6px;
  border-radius: 6px;
  font-weight: 600;
}
.comment-time {
  font-size: 11px;
  color: var(--overlay0);
  margin-left: auto;
}
.comment-bubble {
  font-size: 14px;
  color: var(--subtext1);
  line-height: 1.6;
  word-break: break-word;
  background: var(--surface0);
  border-radius: 0 14px 14px 14px;
  padding: 10px 14px;
  border: 1px solid var(--surface2);
}



/* ══════════════════════════════════
   기존 마피아 게임 뷰 (스타일 유지)
══════════════════════════════════ */
.container { max-width: 900px; margin: 0 auto; padding: 0 24px; }
.game-header { padding: 30px 0 20px; }
.header-info { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.game-title { font-size: 28px; font-weight: 800; color: var(--text); display: flex; align-items: center; gap: 10px; }
.game-type {
  font-size: 12px; color: var(--mauve); background: rgba(203,166,247,0.12);
  padding: 4px 10px; border-radius: 6px; font-weight: 700; letter-spacing: 1px;
}
.game-status-badge {
  font-size: 13px; font-weight: 600; padding: 6px 14px; border-radius: 12px;
}
.game-status-badge.waiting { color: var(--yellow); background: rgba(249,226,175,0.12); }
.game-status-badge.playing { color: var(--green); background: rgba(166,227,161,0.12); }
.game-status-badge.finished { color: var(--overlay1); background: rgba(147,153,178,0.12); }
.winner-banner {
  margin-top: 12px; padding: 12px 20px; border-radius: 12px;
  background: linear-gradient(135deg, rgba(249,226,175,0.1), rgba(250,179,135,0.1));
  color: var(--yellow); font-size: 18px; font-weight: 700;
  border: 1px solid rgba(249,226,175,0.2);
}
.topic-banner {
  margin-top: 12px; padding: 12px 20px; border-radius: 12px;
  background: linear-gradient(135deg, rgba(137,180,250,0.1), rgba(116,199,236,0.1));
  color: var(--blue); font-size: 16px; font-weight: 600;
  border: 1px solid rgba(137,180,250,0.2);
}
.topic-progress { font-size: 13px; color: var(--overlay1); font-weight: 400; }
.section-title { font-size: 18px; font-weight: 700; color: var(--text); margin: 28px 0 16px; }
.players-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 12px; }
.player-card {
  background: var(--surface0); border: 1px solid var(--surface2);
  border-radius: 14px; padding: 16px; text-align: center; position: relative;
  transition: all 0.2s;
}
.player-card.dead { opacity: 0.4; }
.player-card.bot { border-style: dashed; }
.player-avatar { font-size: 28px; margin-bottom: 8px; }
.player-name { font-size: 13px; font-weight: 600; color: var(--text); margin-bottom: 2px; }
.player-id { font-size: 11px; color: var(--overlay0); margin-bottom: 6px; }
.player-role { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px; display: inline-block; }
.dead-overlay { position: absolute; top: 8px; right: 8px; font-size: 20px; }
.timeline {
  padding: 0 0 40px; max-height: 600px; overflow-y: auto;
  scrollbar-width: thin; scrollbar-color: var(--surface2) var(--surface0);
}
.timeline::-webkit-scrollbar { width: 6px; }
.timeline::-webkit-scrollbar-track { background: var(--surface0); border-radius: 3px; }
.timeline::-webkit-scrollbar-thumb { background: var(--surface2); border-radius: 3px; }
.log-hint { font-size: 12px; color: var(--overlay0); font-weight: 400; }
.timeline-item {
  display: flex; gap: 16px; padding: 10px 0;
  border-left: 2px solid var(--surface2); margin-left: 10px; padding-left: 24px;
  position: relative;
}
.event-marker {
  position: absolute; left: -7px; top: 14px;
  width: 12px; height: 12px; border-radius: 50%;
  background: var(--surface2); border: 2px solid var(--base);
}
.event-marker.speak { background: var(--blue); }
.event-marker.vote { background: var(--mauve); }
.event-marker.eliminate { background: var(--red); }
.event-marker.kill { background: var(--red); }
.event-marker.phase_change { background: var(--yellow); }
.event-marker.game_start { background: var(--green); }
.event-marker.game_end { background: var(--peach); }
.event-marker.player_join { background: var(--teal); }
.event-marker.custom { background: var(--sapphire); }
.event-header { display: flex; gap: 8px; align-items: center; margin-bottom: 4px; }
.event-phase { font-size: 11px; color: var(--overlay0); }
.event-round { font-size: 11px; color: var(--overlay0); background: var(--surface0); padding: 1px 6px; border-radius: 4px; }
.event-body { font-size: 14px; color: var(--text); line-height: 1.5; }
.speaker { font-weight: 600; color: var(--blue); margin-right: 4px; }
.speech-bubble {
  background: var(--surface0); padding: 8px 14px; border-radius: 12px;
  margin-top: 4px; border: 1px solid var(--surface2); font-size: 14px;
  word-break: break-word; overflow-wrap: break-word;
}
.voter { font-weight: 600; color: var(--mauve); }
.vote-target { font-weight: 600; color: var(--peach); }
.role-reveal { color: var(--overlay1); font-size: 12px; }
.bot-tag { font-size: 12px; }
.phase-event { font-size: 15px; font-weight: 600; color: var(--yellow); }
.phase-icon { margin-right: 4px; }
.eliminate-event { color: var(--red); }
.kill-event { color: var(--red); }
.end-event { font-size: 18px; font-weight: 700; color: var(--yellow); padding: 8px 0; }
.start-event { font-size: 15px; font-weight: 600; color: var(--green); }
.join-event { color: var(--teal); }
.custom-event { font-size: 15px; font-weight: 600; color: var(--sapphire); }
.loading-state { text-align: center; padding: 100px; }
.spinner {
  width: 40px; height: 40px; border: 3px solid var(--surface2);
  border-top-color: var(--blue); border-radius: 50%;
  animation: spin 0.8s linear infinite; margin: 0 auto;
}
.small-spinner {
  width: 20px; height: 20px; border-width: 2px;
}
.loading-more {
  display: flex; align-items: center; justify-content: center;
  gap: 10px; padding: 20px 0; color: var(--subtext1); font-size: 14px;
}
@keyframes spin { to { transform: rotate(360deg); } }
</style>
